import React, { useState, useEffect } from 'react';
import './GerenciadorUsuarios.css';
import { usersAPI } from '../utils/api';

const GerenciadorUsuarios = ({ onNavigate }) => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedUsuario, setSelectedUsuario] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    senha: '',
    confirmarSenha: '',
    cargo: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 15;

  // Carregar dados iniciais
  useEffect(() => {
    loadUsuarios();
  }, []);

  const loadUsuarios = async () => {
    setLoading(true);
    setError('');
    
    try {
      const data = await usersAPI.list();
      if (data.success) {
        setUsuarios(data.usuarios || []);
        setCurrentPage(1);
      } else {
        setError('Erro ao carregar usuários: ' + (data.error || 'Erro desconhecido'));
      }
    } catch (error) {
      setError('Erro ao conectar com o servidor: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpar erro do campo
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    } else if (formData.nome.length < 3) {
      newErrors.nome = 'Nome deve ter pelo menos 3 caracteres';
    }
    
    if (!formData.cargo) {
      newErrors.cargo = 'Cargo é obrigatório';
    }
    
    if (!formData.senha.trim()) {
      newErrors.senha = 'Senha é obrigatória';
    } else if (formData.senha.length < 6) {
      newErrors.senha = 'Senha deve ter pelo menos 6 caracteres';
    }
    
    if (!formData.confirmarSenha.trim()) {
      newErrors.confirmarSenha = 'Confirmação de senha é obrigatória';
    } else if (formData.senha !== formData.confirmarSenha) {
      newErrors.confirmarSenha = 'Senhas não coincidem';
    }
    
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const openModal = (usuario) => {
    setSelectedUsuario(usuario);
    setFormData({
      nome: usuario.nome,
      senha: '',
      confirmarSenha: '',
      cargo: usuario.cargo
    });
    setFormErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedUsuario(null);
    setFormData({
      nome: '',
      senha: '',
      confirmarSenha: '',
      cargo: ''
    });
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    setSuccess('');
    
    try {
      const data = await usersAPI.update({
        id: selectedUsuario.id,
        nome: formData.nome,
        senha: formData.senha,
        cargo: formData.cargo
      });
      
      if (data.success) {
        setSuccess('Usuário atualizado com sucesso!');
        closeModal();
        loadUsuarios(); // Recarregar lista
      } else {
        setError(data.error || 'Erro ao atualizar usuário');
      }
    } catch (error) {
      setError('Erro ao conectar com o servidor: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleAtivo = async (usuario) => {
    setError('');
    setSuccess('');
    try {
      const novoAtivo = usuario.ativo === 1 ? 0 : 1;
      const data = await usersAPI.toggleActive(usuario.id, novoAtivo);
      if (data.success) {
        setSuccess(novoAtivo === 1 ? 'Usuário ativado com sucesso!' : 'Usuário desativado com sucesso!');
        await loadUsuarios();
      } else {
        setError(data.error || 'Erro ao alterar status do usuário');
      }
    } catch (error) {
      setError('Erro ao conectar com o servidor: ' + error.message);
    }
  };

  const handleDeleteUsuario = async (usuario) => {
    if (!window.confirm(`Tem certeza que deseja excluir o usuário "${usuario.nome}"? Esta ação não pode ser desfeita.`)) {
      return;
    }
    setError('');
    setSuccess('');
    try {
      const data = await usersAPI.remove(usuario.id);
      if (data.success) {
        setSuccess('Usuário excluído com sucesso!');
        await loadUsuarios();
      } else {
        setError(data.error || 'Erro ao excluir usuário');
      }
    } catch (error) {
      setError('Erro ao conectar com o servidor: ' + error.message);
    }
  };

  const getCargoLabel = (cargo) => {
    return cargo === 'administrador' ? 'Administrador' : 'Operador';
  };

  const getCargoClass = (cargo) => {
    return cargo === 'administrador' ? 'cargo-admin' : 'cargo-operador';
  };

  // Filtro por email e paginação
  const filteredUsuarios = usuarios.filter(u =>
    !searchEmail || (u.email || '').toLowerCase().includes(searchEmail.toLowerCase())
  );
  const totalPages = Math.max(1, Math.ceil(filteredUsuarios.length / PAGE_SIZE));
  const page = Math.min(currentPage, totalPages);
  const startIndex = (page - 1) * PAGE_SIZE;
  const visibleUsuarios = filteredUsuarios.slice(startIndex, startIndex + PAGE_SIZE);

  const gotoPrev = () => setCurrentPage(p => Math.max(1, p - 1));
  const gotoNext = () => setCurrentPage(p => Math.min(totalPages, p + 1));
  const onSearchChange = (e) => {
    setSearchEmail(e.target.value);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="gerenciador-usuarios">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando usuários...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gerenciador-usuarios">
      <div className="page-header">
        <h1>👥 Gerenciar Usuários</h1>
        <p>Apenas administradores podem gerenciar usuários</p>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✅</span>
          {success}
        </div>
      )}

      <div className="usuarios-toolbar">
        <input
          type="text"
          className="search-input"
          placeholder="Buscar por e-mail..."
          value={searchEmail}
          onChange={onSearchChange}
        />
        <div style={{ flex: 1 }} />
        <div className="pagination">
          <button className="page-btn" onClick={gotoPrev} disabled={page === 1}>◀</button>
          <span className="page-info">Página {page} de {totalPages}</span>
          <button className="page-btn" onClick={gotoNext} disabled={page === totalPages}>▶</button>
          <button 
            className="btn btn-secondary"
            onClick={loadUsuarios}
          >
            <span className="btn-icon">🔄</span>
            Atualizar
          </button>
        </div>
      </div>

      <div className="usuarios-table-container">
        <table className="usuarios-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nome</th>
              <th>Email</th>
              <th>Cargo</th>
              <th>Operação</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {visibleUsuarios.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  <div className="no-data-content">
                    <span className="no-data-icon">👤</span>
                    <p>Nenhum usuário encontrado</p>
                  </div>
                </td>
              </tr>
            ) : (
              visibleUsuarios.map(usuario => (
                <tr key={usuario.id}>
                  <td>{usuario.id}</td>
                  <td>
                    <div className="user-info">
                      <span className="user-avatar">👤</span>
                      <span className="user-name">{usuario.nome}</span>
                    </div>
                  </td>
                  <td>{usuario.email}</td>
                  <td>
                    <span className={`cargo-badge ${getCargoClass(usuario.cargo)}`}>
                      {getCargoLabel(usuario.cargo)}
                    </span>
                  </td>
                  <td>
                    <div className="operacao-info">
                      {usuario.operador_nome ? (
                        <div>
                          <span className="operacao-nome">{usuario.operador_nome}</span>
                          <span className="operacao-asc">ASC: {usuario.asc_code}</span>
                        </div>
                      ) : (
                        <span className="operacao-sem-vinculo">Sem operação</span>
                      )}
                    </div>
                  </td>
                  <td>
                    {usuario.ativo === 1 ? (
                      <span className="status-badge status-active">Ativo</span>
                    ) : (
                      <span className="status-badge status-inactive">Inativo</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn btn-edit btn-sm"
                        onClick={() => openModal(usuario)}
                        title="Editar usuário"
                      >
                        <span className="btn-icon">✏️</span>
                        Editar
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleToggleAtivo(usuario)}
                        title={usuario.ativo === 1 ? 'Desativar login' : 'Ativar login'}
                      >
                        <span className="btn-icon">{usuario.ativo === 1 ? '⏸️' : '▶️'}</span>
                        {usuario.ativo === 1 ? 'Desativar' : 'Ativar'}
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteUsuario(usuario)}
                        title="Excluir login"
                      >
                        <span className="btn-icon">🗑️</span>
                        Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal de Edição */}
      {showModal && selectedUsuario && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✏️ Editar Usuário</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <form className="modal-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="nome" className="form-label">
                  <span className="label-icon">👤</span>
                  Nome Completo
                </label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.nome ? 'error' : ''}`}
                  placeholder="Ex: João Silva"
                  disabled={isSubmitting}
                />
                {formErrors.nome && (
                  <span className="field-error">{formErrors.nome}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="cargo" className="form-label">
                  <span className="label-icon">👔</span>
                  Cargo
                </label>
                <select
                  id="cargo"
                  name="cargo"
                  value={formData.cargo}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.cargo ? 'error' : ''}`}
                  disabled={isSubmitting}
                >
                  <option value="">Selecione o cargo</option>
                  <option value="administrador">Administrador</option>
                  <option value="operador">Operador</option>
                </select>
                {formErrors.cargo && (
                  <span className="field-error">{formErrors.cargo}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="senha" className="form-label">
                  <span className="label-icon">🔒</span>
                  Nova Senha
                </label>
                <input
                  type="password"
                  id="senha"
                  name="senha"
                  value={formData.senha}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.senha ? 'error' : ''}`}
                  placeholder="Mínimo 6 caracteres"
                  disabled={isSubmitting}
                />
                {formErrors.senha && (
                  <span className="field-error">{formErrors.senha}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="confirmarSenha" className="form-label">
                  <span className="label-icon">🔒</span>
                  Confirmar Senha
                </label>
                <input
                  type="password"
                  id="confirmarSenha"
                  name="confirmarSenha"
                  value={formData.confirmarSenha}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.confirmarSenha ? 'error' : ''}`}
                  placeholder="Digite a senha novamente"
                  disabled={isSubmitting}
                />
                {formErrors.confirmarSenha && (
                  <span className="field-error">{formErrors.confirmarSenha}</span>
                )}
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                  disabled={isSubmitting}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className={`btn btn-primary ${isSubmitting ? 'loading' : ''}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="loading-spinner"></div>
                      Salvando...
                    </>
                  ) : (
                    <>
                      <span className="btn-icon">💾</span>
                      Salvar Alterações
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GerenciadorUsuarios;
